import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow, IconPlus } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import NavList from '../layout/NavList/NavList'
import {
  TextSearchList,
  TextSearchWindowBody,
  textSearchLabel,
} from '../components/globaltextsearch'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { useTextSearchCache } from '../hooks/useTextSearchCache'
import { useModal } from '../context/ModalContext'
import { ItemBookmarkProvider } from '../components/references/ItemBookmarkContext'
import { deleteTextSearch } from '../services/textSearches'
import { usePageTitle } from '../components/DocumentTitle'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconEdit = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const IconDelete = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

// Global Text Search workspace: a collapsible sidebar (saved-search list) beside
// a tiled window grid. Each window loads one saved search (its own rule builder,
// results, and PDF viewer), so several can be open at once. Mirrors the Global AI
// Search page; a search is defined by literal Find/Exempt rules rather than a chat.
function GlobalTextSearchPage() {
  const { openModal } = useModal()
  const [reloadSignal, setReloadSignal] = useState(0)
  const [notice, setNotice] = useState('')

  const grid = useWindowGrid({
    storageKey: 'ct-grid-globaltextsearch-v1',
    limits: { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 },
    initialType: 'textsearch',
  })

  // Full search docs for every window that has one (fetched on reload, seeded on
  // selection). Empty windows (entityId null) contribute nothing to fetch.
  const entityIds = useMemo(
    () => grid.columns.flatMap((col) => col.windows.map((w) => w.entityId)).filter(Boolean),
    [grid.columns]
  )
  const { searchesById, seed, remove } = useTextSearchCache(entityIds)

  // Sidebar highlight tracks the active window's search.
  const activeSearchId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((w) => w.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  // Auto-dismiss the "too many windows" notice.
  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(timer)
  }, [notice])

  // Load a search into the ACTIVE window. We already hold the doc, so seed the
  // cache to skip a fetch. Never creates a window.
  const openSearch = useCallback(
    (search) => {
      if (!search?._id) return
      seed(search)
      grid.selectEntity({ type: 'textsearch', entityId: search._id })
    },
    [seed, grid]
  )
  const openSearchRef = useRef(openSearch)
  openSearchRef.current = openSearch

  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    grid.addWindow({ type: 'textsearch' })
  }, [grid])

  // Any window showing a deleted search reverts to the empty state (kept, not closed).
  const clearWindowsForSearch = useCallback(
    (searchId) => {
      grid.columns.forEach((col) =>
        col.windows.forEach((w) => {
          if (w.entityId === searchId) grid.setWindowEntity(w.id, null)
        })
      )
    },
    [grid]
  )

  const handleNewSearch = () => {
    openModal('create-text-search', {
      onCreated: (created) => {
        setReloadSignal((n) => n + 1)
        if (created) openSearchRef.current(created)
      },
    })
  }

  const handleRenameSearch = useCallback(
    (search) => {
      if (!search) return
      openModal('edit-text-search', {
        search,
        onSaved: (updated) => {
          seed(updated)
          setReloadSignal((n) => n + 1)
        },
      })
    },
    [openModal, seed]
  )

  const handleDeleteSearch = useCallback(
    (search) => {
      if (!search) return
      openModal('confirm', {
        title: 'Delete search',
        message: `Delete "${textSearchLabel(search)}"? This can't be undone.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => deleteTextSearch(search._id),
        onConfirmed: () => {
          clearWindowsForSearch(search._id)
          remove(search._id)
          setReloadSignal((n) => n + 1)
        },
      })
    },
    [openModal, clearWindowsForSearch, remove]
  )

  // Seed the cache + refresh the sidebar when a window saves its rules.
  const handleSaved = useCallback(
    (updated) => {
      if (!updated) return
      seed(updated)
      setReloadSignal((n) => n + 1)
    },
    [seed]
  )

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      const search = searchesById[win.entityId]
      return search ? textSearchLabel(search) : 'Loading…'
    },
    [searchesById]
  )
  usePageTitle(activeSearchId ? getTitle({ entityId: activeSearchId }) : '')

  const renderWindow = useCallback(
    (win) => {
      if (!win.entityId) {
        return <div className="display-empty">Select or create a search.</div>
      }
      return (
        <TextSearchWindowBody
          search={searchesById[win.entityId]}
          panes={win.panes}
          onPanesChange={(layout) => grid.setPanes(win.id, layout)}
          onSaved={handleSaved}
        />
      )
    },
    [searchesById, grid, handleSaved]
  )

  const renderMenuItems = useCallback(
    (win) => {
      const search = win.entityId ? searchesById[win.entityId] : null
      if (!search) return null
      return (
        <>
          <button type="button" role="menuitem" onClick={() => handleRenameSearch(search)}>
            <IconEdit />
            Rename search
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => handleDeleteSearch(search)}
          >
            <IconDelete />
            Delete search
          </button>
        </>
      )
    },
    [searchesById, handleRenameSearch, handleDeleteSearch]
  )

  return (
    <ItemBookmarkProvider>
    <MainLayout
      storageId="global-text-search-layout"
      navLocation={<NavLocation title="Global Text Search" />}
      navActions={[
        {
          key: 'add-window',
          label: 'Add window',
          icon: <IconAddWindow />,
          onClick: handleAddWindow,
        },
        {
          key: 'new',
          label: 'New search',
          icon: <IconPlus />,
          onClick: handleNewSearch,
        },
      ]}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          <TextSearchList
            reloadSignal={reloadSignal}
            selectedId={activeSearchId}
            onSelect={openSearch}
          />
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice && <div className="sr-grid-notice">{notice}</div>}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              renderMenuItems={renderMenuItems}
              empty="Select or create a search to get started."
            />
          </div>
        </div>
      }
    />
    </ItemBookmarkProvider>
  )
}

export default GlobalTextSearchPage
